import type {
  UsersAccessTechnicianFormErrors,
  UsersAccessTechnicianFormValuesI,
} from '../../types/usersAccess'
import { useTranslation } from 'react-i18next'

import {
  MultiSelectInput,
  Stack,
  Text,
  TextInput,
  errorColorFor,
} from '@hivewatch/dark'
import type { ColorScheme, MultiSelectOption } from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'

interface UsersAccessTechnicianFormIProps {
  colorScheme: ColorScheme
  errors: UsersAccessTechnicianFormErrors
  facilityOptions: MultiSelectOption[]
  onFacilitySearchChange: (query: string) => void
  selectedFacilityOptions?: MultiSelectOption[]
  setField: <FieldT extends keyof UsersAccessTechnicianFormValuesI>(
    field: FieldT,
    value: UsersAccessTechnicianFormValuesI[FieldT]
  ) => void
  testIdPrefix: string
  values: UsersAccessTechnicianFormValuesI
}

const toggleValue = <T extends string>(values: T[], value: T) =>
  values.includes(value)
    ? values.filter((current) => current !== value)
    : [...values, value]

export const UsersAccessTechnicianForm = ({
  colorScheme,
  errors,
  facilityOptions,
  onFacilitySearchChange,
  selectedFacilityOptions = [],
  setField,
  testIdPrefix,
  values,
}: UsersAccessTechnicianFormIProps) => {
  const { t } = useTranslation()
  const deviceTypeOptions: MultiSelectOption[] = [
    {
      value: DeviceType.Camera,
      label: t('settings:rebrand_users_device_type_camera'),
    },
    {
      value: DeviceType.Door,
      label: t('settings:rebrand_users_device_type_door'),
    },
  ]

  return (
    <Stack gap='16px'>
      <TextInput
        colorScheme={colorScheme}
        data-testid={`${testIdPrefix}:firstName`}
        hasWarning={Boolean(errors.firstName)}
        inputProps={{ autoComplete: 'given-name' }}
        label={t('settings:rebrand_users_first_name_label')}
        onValueChange={(value) => setField('firstName', value)}
        placeholder={t('settings:rebrand_users_first_name_placeholder')}
        value={values.firstName}
        warningText={errors.firstName}
        width='100%'
      />
      <TextInput
        colorScheme={colorScheme}
        data-testid={`${testIdPrefix}:lastName`}
        hasWarning={Boolean(errors.lastName)}
        inputProps={{ autoComplete: 'family-name' }}
        label={t('settings:rebrand_users_last_name_label')}
        onValueChange={(value) => setField('lastName', value)}
        placeholder={t('settings:rebrand_users_last_name_placeholder')}
        value={values.lastName}
        warningText={errors.lastName}
        width='100%'
      />
      <TextInput
        colorScheme={colorScheme}
        data-testid={`${testIdPrefix}:email`}
        hasWarning={Boolean(errors.email)}
        inputProps={{ autoComplete: 'email', type: 'email' }}
        label={t('settings:rebrand_users_email_label')}
        onValueChange={(value) => setField('email', value)}
        placeholder={t('settings:rebrand_users_email_placeholder')}
        value={values.email}
        warningText={errors.email}
        width='100%'
      />
      <Stack gap='4px'>
        <MultiSelectInput
          colorScheme={colorScheme}
          data-testid={`${testIdPrefix}:facilities`}
          // The facility search is server-side and already matches by
          // address, so an address-only match's label may not contain the
          // typed query — 'internal' mode would re-filter it away.
          filterMode='external'
          label={t('settings:rebrand_users_facilities_label')}
          onClear={() => setField('facilityIds', [])}
          onQueryChange={onFacilitySearchChange}
          onRemove={(value) =>
            setField(
              'facilityIds',
              values.facilityIds.filter((id) => id !== value)
            )
          }
          onToggleOption={(value) =>
            setField('facilityIds', toggleValue(values.facilityIds, value))
          }
          options={facilityOptions}
          placeholder={t('settings:rebrand_users_facilities_placeholder')}
          searchPlaceholder={t(
            'settings:rebrand_users_facilities_search_placeholder'
          )}
          searchable
          selected={values.facilityIds}
          selectedOptions={selectedFacilityOptions}
          usePortal
        />
        {errors.facilityIds && (
          <Text
            color={errorColorFor(colorScheme)}
            role='alert'
            textStyle='caption'
          >
            {errors.facilityIds}
          </Text>
        )}
      </Stack>
      <Stack gap='4px'>
        <MultiSelectInput
          colorScheme={colorScheme}
          data-testid={`${testIdPrefix}:deviceTypes`}
          label={t('settings:rebrand_users_device_types_label')}
          onClear={() => setField('deviceTypes', [])}
          onRemove={(value) =>
            setField(
              'deviceTypes',
              values.deviceTypes.filter(
                (type) => type !== (value as DeviceType)
              )
            )
          }
          onToggleOption={(value) =>
            setField(
              'deviceTypes',
              toggleValue(values.deviceTypes, value as DeviceType)
            )
          }
          options={deviceTypeOptions}
          placeholder={t('settings:rebrand_users_device_types_placeholder')}
          searchPlaceholder={t(
            'settings:rebrand_users_device_types_search_placeholder'
          )}
          searchable
          selected={values.deviceTypes}
          usePortal
        />
        {errors.deviceTypes && (
          <Text
            color={errorColorFor(colorScheme)}
            role='alert'
            textStyle='caption'
          >
            {errors.deviceTypes}
          </Text>
        )}
      </Stack>
    </Stack>
  )
}
